import { FC, MouseEvent, useCallback, useState } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { ConnectionHandler, RecordSourceSelectorProxy } from 'relay-runtime';
import graphql from 'babel-plugin-relay/macro';
import { Form, Formik, FormikHelpers } from 'formik';
import { date, number, object, string } from 'yup';
import format from 'date-fns/format';

import Box from '@mui/material/Box';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import OrderEditMainPaymentsAdd from 'components/OrderEditMainPaymentsAdd';
import PaymentRow from 'components/PaymentRow';

import { useCurrentUser } from 'components/CurrentUserProvider';

import {
  OrderEditMainPaymentsTableAddMutation,
  OrderEditMainPaymentsTableAddMutation$variables,
  PaymentStatus,
  PaymentType,
} from '__generated__/OrderEditMainPaymentsTableAddMutation.graphql';
import { OrderEditMainPaymentsTableDeleteMutation } from '__generated__/OrderEditMainPaymentsTableDeleteMutation.graphql';
import { OrderEditMainPaymentsTable_order$key } from '__generated__/OrderEditMainPaymentsTable_order.graphql';

type FormValues = {
  paymentDate: Date | null;
  type: PaymentType;
  details: string;
  amount: string;
};

type Props = {
  order: OrderEditMainPaymentsTable_order$key;
};

const initial: FormValues = {
  paymentDate: new Date(),
  type: 'BANK_TRANSFER',
  details: '',
  amount: '',
};

const validaton = object({
  paymentDate: date().required('Privaloma'),
  type: string().required('Privaloma'),
  details: string(),
  amount: number().required('Privaloma').typeError('Veskite skaičų'),
});

type Payment = {
  paymentDate: string | null;
  type: PaymentType;
  details?: string | null;
  amount: number;
  status?: PaymentStatus | null;
};

type User = {
  name: string;
};

function createPaymentRecord(
  store: RecordSourceSelectorProxy,
  payment: Payment,
  currentUser: User
) {
  const { paymentDate, type, details, amount, status } = payment;
  const id = 'tempPayment';

  const paymentRecord = store.create(id, 'Payment');

  paymentRecord
    .setValue(id, 'id')
    .setValue(paymentDate, 'paymentDate')
    .setValue(type, 'type')
    .setValue(details, 'details')
    .setValue(amount, 'amount')
    .setValue(status, 'status')
    .setValue('PAID', 'status')
    .setValue('dabar', 'createdAt')
    .getOrCreateLinkedRecord('user', 'User')
    .setValue(currentUser.name, 'name');

  return paymentRecord;
}

const OrderEditPaymentsTable: FC<Props> = props => {
  const [adding, setAdding] = useState(false);
  const currentUser = useCurrentUser();

  const { id, payments } = useFragment(
    graphql`
      fragment OrderEditMainPaymentsTable_order on Order {
        id
        payments(first: 2147483647)
          @connection(key: "OrderEditPayments_order_payments") {
          edges {
            node {
              id
              ...PaymentRow_payment
            }
          }
        }
      }
    `,
    props.order
  );

  const connectionID = ConnectionHandler.getConnectionID(
    id,
    'OrderEditPayments_order_payments'
  );

  const [add] = useMutation<OrderEditMainPaymentsTableAddMutation>(graphql`
    mutation OrderEditMainPaymentsTableAddMutation(
      $id: ID!
      $payment: AddPaymentInput!
      $connections: [ID!]!
    ) {
      addPaymentToOrder(id: $id, payment: $payment) {
        node
          @appendNode(connections: $connections, edgeTypeName: "PaymentEdge") {
          id
          ...PaymentRow_payment
        }
      }
    }
  `);

  const [remove] =
    useMutation<OrderEditMainPaymentsTableDeleteMutation>(graphql`
      mutation OrderEditMainPaymentsTableDeleteMutation(
        $id: ID!
        $connections: [ID!]!
      ) {
        deleteNode(id: $id) @deleteEdge(connections: $connections)
      }
    `);

  const handleAddClick = useCallback((event: MouseEvent) => {
    event.preventDefault();
    setAdding(true);
  }, []);

  const handleCancel = useCallback(() => {
    setAdding(false);
  }, []);

  const handleSubmit = useCallback(
    (values: FormValues, helpers: FormikHelpers<FormValues>) => {
      helpers.resetForm();
      setAdding(false);

      const payment: OrderEditMainPaymentsTableAddMutation$variables['payment'] =
        {
          ...values,
          paymentDate: format(values.paymentDate!, 'yyyy-MM-dd'),
          amount: Number(values.amount),
          status: 'PAID',
        };

      add({
        variables: { connections: [connectionID], id, payment },
        optimisticUpdater: store => {
          const connectionRecord = store.get(connectionID);
          if (!connectionRecord) return;

          const newPaymentRecord = createPaymentRecord(
            store,
            payment,
            currentUser
          );

          const newEdge = ConnectionHandler.createEdge(
            store,
            connectionRecord,
            newPaymentRecord,
            'PaymentEdge'
          );

          ConnectionHandler.insertEdgeAfter(connectionRecord, newEdge);
        },
      });
    },
    [add, connectionID, currentUser, id]
  );

  const handleDelete = useCallback(
    (paymentId: string) => {
      remove({
        variables: { connections: [connectionID], id: paymentId },
        optimisticUpdater: store => {
          const orderRecord = store.get(id);
          if (!orderRecord) return;

          const connectionRecord = ConnectionHandler.getConnection(
            orderRecord,
            'OrderEditPayments_order_payments'
          );
          if (!connectionRecord) return;

          ConnectionHandler.deleteNode(connectionRecord, paymentId);
        },
      });
    },
    [connectionID, id, remove]
  );

  return (
    <Box sx={{ pt: 2 }}>
      <Typography variant="caption">Atlikti mokėjimai</Typography>

      <Formik
        initialValues={initial}
        onSubmit={handleSubmit}
        validationSchema={validaton}>
        <Form>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="center">Būdas</TableCell>
                  <TableCell align="center">Komentaras</TableCell>
                  <TableCell align="center">Būklė</TableCell>
                  <TableCell align="center">Suma</TableCell>
                  <TableCell align="center">Įvedė</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>

              <TableBody>
                {(payments?.edges ?? []).map(edge => {
                  const node = edge?.node;
                  if (!node) return null;

                  return (
                    <PaymentRow
                      key={node.id}
                      payment={node}
                      onDelete={handleDelete}
                    />
                  );
                })}

                {adding ? (
                  <OrderEditMainPaymentsAdd onCancel={handleCancel} />
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAddClick}>
                        Pridėti +
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Form>
      </Formik>
    </Box>
  );
};

export default OrderEditPaymentsTable;
